import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtilsV2 } from '@shared/utils';
import {
  AccountsApi,
  RequestStrategyEnum,
  UsersApi,
  type AccountRequest,
  type AccountResponse,
  type AddUserAccountsRequest,
  type DeleteUserAccountsRequest,
  type WithSignal,
} from '@shared/api';

const getUserAccountsEffect = createEffect(({ signal, ...request }: WithSignal<AccountRequest>) => AccountsApi.getAccounts(request, signal));
const addAccountsEffect = createEffect(async (request: AddUserAccountsRequest) => {
  const user = await UsersApi.addUserAccounts(request);
  if (!user.success) throw new Error(user.message || 'Something went wrong while adding Accounts. Please try again.');
  return { AssignedUserId: request.userIds };
});
const deleteAccountEffect = createEffect(async (request: DeleteUserAccountsRequest) => {
  const user = await UsersApi.deleteUserAccounts(request);
  if (!user.success) throw new Error(user.message || 'Something went wrong while removing Account. Please try again.');
  return { AssignedUserId: request.userIds };
});
const clearEvent = createEvent();

sample({ source: addAccountsEffect.doneData, target: getUserAccountsEffect, fn: (doneData) => ({ AssignedUserId: doneData.AssignedUserId }) });
sample({ source: deleteAccountEffect.doneData, target: getUserAccountsEffect, fn: (doneData) => ({ AssignedUserId: doneData.AssignedUserId }) });

const $dataStore = createStore<AccountResponse[]>([])
  .on(getUserAccountsEffect.done, (current, data) => data.result.data || current)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getUserAccountsEffect.pending, (_, data) => data)
  .on(getUserAccountsEffect.fail, (_, data) => ApiUtilsV2.abortApiLoadingFailCaseTrigger(data.error))
  .on(getUserAccountsEffect.done, () => false)
  .on(addAccountsEffect.pending, (_, data) => data)
  .on(addAccountsEffect.fail, () => false)
  .on(deleteAccountEffect.pending, (_, data) => data)
  .on(deleteAccountEffect.fail, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getUserAccountsEffect, () => null)
  // .on(getUserAccountsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getUserAccountsEffect.fail, () => 'Something went wrong while fetching Accounts. Please try again.')
  .on(getUserAccountsEffect.done, () => null)
  .reset(clearEvent);

const $actionErrorStore = createStore<string | null>(null)
  .on(addAccountsEffect, () => null)
  // .on(addAccountsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addAccountsEffect.fail, () => 'Something went wrong while adding Accounts. Please try again.')
  .on(addAccountsEffect.done, () => null)
  .on(deleteAccountEffect, () => null)
  // .on(deleteAccountEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteAccountEffect.fail, () => 'Something went wrong while deleting Account. Please try again.')
  .on(deleteAccountEffect.done, () => null)
  .reset(clearEvent);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

const getUserAccountsApi = ApiUtilsV2.wrapperApi(getUserAccountsEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getUserAccountsApi.cancel());

export const $userAccountsStore = {
  events: {
    getUserAccounts: getUserAccountsApi.trigger,
    addAccounts: addAccountsEffect,
    deleteAccount: deleteAccountEffect,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
